import './day-picker-styles.css'
import { ChangeEvent, memo, useCallback, useState } from 'react'

import { Box, Divider, TextField, Typography, useTheme } from '@mui/material'
import { format, setHours, setMinutes } from 'date-fns'
import { DateRange, DayPicker } from 'react-day-picker'
import { useController, UseControllerProps } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InputErrorEndAdornment } from '@shared/components/ErrorHandling'
import { FormStructure } from 'domains/Workspaces/Events'

import { formatEventDuration } from './formatEventDuration'

const parseNewDate = (time: string, baseDate: Date) => {
  const [hours, minutes] = time.split(':').map((str) => parseInt(str, 10))
  return setHours(setMinutes(baseDate, minutes), hours)
}

const END_MONTH = new Date(new Date().getFullYear() + 5, 11, 31)

const ControlledDateTimeRangeRaw = ({ control }: Pick<UseControllerProps<FormStructure>, 'control'>) => {
  const { t } = useTranslation(['domain'])
  const theme = useTheme()

  const startTimeController = useController({ control, name: 'manualStartTime' })
  const endTimeController = useController({ control, name: 'manualEndTime' })

  const derivedStartTime = startTimeController.field.value ? new Date(startTimeController.field.value) : undefined
  const derivedEndTime = endTimeController.field.value ? new Date(endTimeController.field.value) : undefined

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | undefined>(
    startTimeController.field.value
      ? {
          from: new Date(startTimeController.field.value),
          ...(!!endTimeController.field.value && { to: new Date(endTimeController.field.value) }),
        }
      : undefined,
  )
  const [startTime, setStartTime] = useState(derivedStartTime ? format(derivedStartTime, 'HH:mm') : '00:00')
  const [endTime, setEndTime] = useState(derivedEndTime ? format(derivedEndTime, 'HH:mm') : '00:00')

  const duration = formatEventDuration(derivedStartTime, derivedEndTime)

  const handleDateRangeChange = useCallback(
    (dateRange?: DateRange) => {
      setSelectedDateRange(dateRange)

      if (dateRange?.from) {
        startTimeController.field.onChange(parseNewDate(startTime, dateRange.from))
      } else {
        startTimeController.field.onChange(undefined)
      }

      if (dateRange?.to) {
        endTimeController.field.onChange(parseNewDate(endTime, dateRange.to))
      } else {
        endTimeController.field.onChange(undefined)
      }
    },
    [endTime, endTimeController.field, startTime, startTimeController.field],
  )

  const handleStartTimeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const time = e.target.value
      setStartTime(time)

      if (selectedDateRange?.from) {
        startTimeController.field.onChange(parseNewDate(time, selectedDateRange.from))
      }
    },
    [selectedDateRange?.from, startTimeController.field],
  )

  const handleEndTimeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const time = e.target.value
      setEndTime(time)

      if (selectedDateRange?.to) {
        endTimeController.field.onChange(parseNewDate(time, selectedDateRange.to))
      }
    },
    [selectedDateRange?.to, endTimeController.field],
  )

  return (
    <Box sx={{ mt: 2, display: 'flex', columnGap: 2, alignItems: 'end' }} data-theme={theme.palette.mode}>
      <DayPicker
        animate
        mode="range"
        selected={selectedDateRange}
        onSelect={handleDateRangeChange}
        showOutsideDays
        captionLayout="dropdown"
        endMonth={END_MONTH}
        components={{
          MonthCaption: (props) => <Typography {...props} variant="heading-base" component="div" />,
          PreviousMonthButton: (props) => <button style={{ width: 18, marginRight: 16 }} {...props} />,
          NextMonthButton: (props) => <button style={{ width: 18, marginRight: 8 }} {...props} />,
        }}
      />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          rowGap: 1,
          alignItems: 'stretch',
        }}
      >
        <Divider sx={{ my: 1 }} flexItem />

        <Typography variant="heading-sm">From (start time on first day):</Typography>
        <TextField
          type="time"
          value={startTime}
          onChange={handleStartTimeChange}
          error={endTimeController.fieldState.invalid}
          InputProps={{
            endAdornment: (
              <InputErrorEndAdornment
                fieldState={startTimeController.fieldState}
                isSubmitted={startTimeController.formState.isSubmitted}
              />
            ),
          }}
        />

        <Typography sx={{ mt: 2 }} variant="heading-sm">
          Until (end time on last day):
        </Typography>
        <TextField
          type="time"
          value={endTime}
          onChange={handleEndTimeChange}
          error={endTimeController.fieldState.invalid}
          InputProps={{
            endAdornment: (
              <InputErrorEndAdornment
                fieldState={endTimeController.fieldState}
                isSubmitted={endTimeController.formState.isSubmitted}
              />
            ),
          }}
        />

        <Divider sx={{ mt: 2, mb: 1 }} flexItem />

        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', columnGap: 1 }}>
          <Typography variant="heading-sm">{t('domain:Workspace.Event.duration')}</Typography>
          <Typography variant="text">{duration ?? '—'}</Typography>
        </Box>
      </Box>
    </Box>
  )
}

export const ControlledDateTimeRange = memo(ControlledDateTimeRangeRaw)
